import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { Search, ShieldCheck, Zap, Lock, Sparkles, FileText, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SiteFooter, SiteHeader } from "@/components/tools/ToolShell";
import { PDF_CATEGORIES, PDF_TOOLS, type PdfCategory } from "@/lib/pdf-catalog";
import { PdfToolIcon } from "@/components/tools/ToolIcon";

export const Route = createFileRoute("/pdf-tools/")({
  head: () => ({
    meta: [
      { title: "PDF Toolkit Pro — Free Online Client-Side PDF Tools | Picamini.online" },
      {
        name: "description",
        content:
          "26+ free client-side PDF tools: merge, split, compress, edit, convert, sign, protect and OCR PDF. 100% private — your files never leave your browser.",
      },
      {
        name: "keywords",
        content:
          "pdf tools, merge pdf, split pdf, compress pdf, edit pdf online, pdf to word, image to pdf, secure pdf, private pdf tools, picamini pdf",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "PDF Toolkit Pro — Free Client-Side PDF Tools" },
      {
        property: "og:description",
        content:
          "Fast, 100% private PDF tools. Merge, split, compress, edit & convert PDFs directly in your browser with zero server uploads.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/pdf-tools" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "PDF Toolkit Pro - Free Client-Side PDF Tools on Picamini.online" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "PDF Toolkit Pro — Free Client-Side PDF Tools" },
      { name: "twitter:description", content: "Merge, split, compress, edit, convert, sign, protect, and OCR PDFs in your browser." },
      { name: "twitter:image", content: "https://picamini.online/og-image.png" },
      { name: "theme-color", content: "#4F46E5" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [{ rel: "canonical", href: "https://picamini.online/pdf-tools" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "PDF Toolkit Pro by Picamini.online",
          "applicationCategory": "UtilitiesApplication",
          "operatingSystem": "Any (Web Browser)",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "description": "A suite of 26+ free client-side PDF utility tools including PDF merger, splitter, compressor, editor, OCR, digital signer, and converters."
        })
      }
    ]
  }),
  component: PdfHub,
});

function PdfHub() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<PdfCategory | "All">("All");

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const catParam = params.get("cat");
      const qParam = params.get("q");
      if (catParam && (PDF_CATEGORIES as readonly string[]).includes(catParam)) {
        setCategory(catParam as PdfCategory);
      }
      if (qParam) {
        setQuery(qParam);
      }
    }
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PDF_TOOLS.filter((t) => {
      const matchesCategory = category === "All" || t.category === category;
      const matchesQuery =
        !q ||
        t.name.toLowerCase().includes(q) ||
        t.tagline.toLowerCase().includes(q) ||
        t.keywords.some((k) => k.includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [query, category]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        {/* Hero Section */}
        <section className="bg-hero-gradient text-primary-foreground">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1 text-xs font-bold uppercase tracking-wider backdrop-blur mb-4">
              <ShieldCheck className="size-4 text-emerald-300" />
              100% Local & Private — Zero Server Uploads
            </div>
            <h1 className="mx-auto max-w-4xl font-display text-4xl font-extrabold sm:text-5xl lg:text-6xl tracking-tight leading-tight">
              PDF Toolkit Pro
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg opacity-90 leading-relaxed">
              Every tool runs entirely inside your web browser. Merge, split, compress, edit, convert, sign, and lock PDF files with maximum speed and zero file uploads.
            </p>

            {/* Search Input */}
            <div className="mx-auto mt-8 flex max-w-lg items-center gap-2 rounded-2xl bg-white/20 p-2 shadow-lg backdrop-blur border border-white/20">
              <Search className="size-5 ml-2 opacity-80 shrink-0" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search PDF tools (e.g. merge, compress, protect, word to pdf)…"
                aria-label="Search PDF tools"
                className="border-0 bg-transparent text-primary-foreground placeholder:text-primary-foreground/70 focus-visible:ring-0 text-sm sm:text-base font-medium"
              />
            </div>

            {/* Security Pills */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-xs sm:text-sm font-semibold opacity-90">
              <span className="flex items-center gap-1.5"><Lock className="size-4 text-emerald-300" /> Files Stay On Your PC</span>
              <span className="flex items-center gap-1.5"><Zap className="size-4 text-amber-300" /> Instant Browser Processing</span>
              <span className="flex items-center gap-1.5"><Sparkles className="size-4 text-cyan-300" /> 100% Free Forever</span>
            </div>
          </div>
        </section>

        {/* Filter Categories & Tools Grid */}
        <section className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 py-12 space-y-8">
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
            <Button
              size="sm"
              variant={category === "All" ? "default" : "outline"}
              onClick={() => setCategory("All")}
              className="rounded-xl font-bold"
            >
              All PDF Tools ({PDF_TOOLS.length})
            </Button>
            {PDF_CATEGORIES.map((c) => (
              <Button
                key={c}
                size="sm"
                variant={category === c ? "default" : "outline"}
                onClick={() => setCategory(c)}
                className="rounded-xl font-semibold"
              >
                {c}
              </Button>
            ))}
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((tool) => (
              <Link
                key={tool.slug}
                to="/pdf-tools/$slug"
                params={{ slug: tool.slug }}
                className="surface-card group flex flex-col justify-between gap-4 p-6 rounded-2xl border bg-card transition-all hover:-translate-y-1 hover:shadow-xl hover:border-primary/40"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="grid size-12 place-items-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground shadow-sm">
                      <PdfToolIcon name={tool.icon} className="size-6" />
                    </span>
                    {tool.featured ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-500/30 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                        <Sparkles className="size-3 text-amber-500" /> Popular
                      </span>
                    ) : null}
                  </div>
                  <div>
                    <h2 className="font-display text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                      {tool.name}
                    </h2>
                    <p className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {tool.tagline}
                    </p>
                  </div>
                </div>

                <div className="pt-3 border-t border-border/60 flex items-center justify-between text-xs font-bold text-primary group-hover:translate-x-0.5 transition-transform">
                  <span>Open PDF Tool</span>
                  <ArrowRight className="size-4" />
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="py-16 text-center space-y-3">
              <p className="text-lg font-semibold text-muted-foreground">
                No PDF tools match “{query}”.
              </p>
              <Button variant="outline" onClick={() => { setQuery(""); setCategory("All"); }}>
                Clear Search Filter
              </Button>
            </div>
          ) : null}
        </section>

        {/* ============ SEO Rich Informational Content Section ============ */}
        <section className="border-t border-border/60 bg-secondary/20 py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <article className="prose prose-slate dark:prose-invert max-w-none space-y-12">
              {/* Main H2 Header */}
              <header className="space-y-3 text-center sm:text-left">
                <span className="text-xs font-bold uppercase tracking-widest text-primary">
                  100% Private Client-Side Document Management Suite
                </span>
                <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl text-foreground">
                  The Complete Free PDF Utilities & Document Processing Suite
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Managing PDF files should not mean uploading confidential documents to mystery cloud servers. Picamini.online’s <strong>PDF Toolkit Pro</strong> provides 26+ powerful PDF conversion, editing, compression, and security tools that run <strong>entirely inside your web browser</strong> using WebAssembly, WebAudio, and WebGL APIs. Zero file uploads, zero registration, and zero data leakage.
                </p>
              </header>

              {/* Tool Category 1: Organize & Edit */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 shadow-xs">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  1. Organize, Reorder, Edit & Watermark PDF Pages
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Take full control over your document layout. Combine multiple PDF reports into one master file, split large ebooks into custom page ranges, delete blank pages, or rewrite text in place with our full PDF Editor.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                  <Link to="/pdf-tools/$slug" params={{ slug: "merge-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>📄</span> Merge PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "split-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>✂️</span> Split PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "organize-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🗂️</span> Organize PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "rotate-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🔄</span> Rotate PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "extract-pages" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>📥</span> Extract Pages
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "remove-pages" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🗑️</span> Remove Pages
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "edit-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>✏️</span> Edit PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "page-numbers" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🔢</span> Page Numbers
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "watermark-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>💧</span> Watermark PDF
                  </Link>
                </div>
              </div>

              {/* Tool Category 2: Optimize & Repair */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 shadow-xs">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  2. Optimize PDF Size, Grayscale & Repair Damaged Files
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Heavy PDFs often get rejected by email attachments or online submission portals. Shrink file sizes up to 80% without destroying readability, convert color pages to monochrome grayscale to save printer ink, or repair corrupted PDF structures.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-3 text-sm">
                  <Link to="/pdf-tools/$slug" params={{ slug: "compress-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>📦</span> Compress PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "grayscale-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🌗</span> Grayscale PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "repair-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🔧</span> Repair PDF
                  </Link>
                </div>
              </div>

              {/* Tool Category 3: Convert to PDF */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 shadow-xs">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  3. Convert Word, Excel, PowerPoint, Images & HTML to PDF
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Convert all your digital document formats into clean, standardized PDF files. Turn smartphone photos (JPG, PNG, WEBP) into printable multi-page PDFs, or export Word DOCX files, Excel spreadsheets, and HTML web pages instantly.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                  <Link to="/pdf-tools/$slug" params={{ slug: "image-to-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🖼️</span> Images to PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "word-to-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>📘</span> Word to PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "excel-to-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>📊</span> Excel to PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "ppt-to-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>📙</span> PowerPoint to PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "html-to-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🌐</span> HTML to PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "txt-to-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>📝</span> Text to PDF
                  </Link>
                </div>
              </div>

              {/* Tool Category 4: Convert from PDF */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 shadow-xs">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  4. Extract PDF Content to Word, Excel, Images & Text
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Need to reuse data locked inside a PDF? Convert pages into editable Word DOCX files, extract data tables into Excel XLSX spreadsheets, export high-res PNG/JPG images, or pull raw plain text.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                  <Link to="/pdf-tools/$slug" params={{ slug: "pdf-to-image" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🖼️</span> PDF to Images
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "pdf-to-word" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>📝</span> PDF to Word
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "pdf-to-excel" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>📈</span> PDF to Excel
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "pdf-to-text" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>📃</span> PDF to Text
                  </Link>
                </div>
              </div>

              {/* Tool Category 5: Security & Utilities */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 shadow-xs">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  5. Password Protection, Digital Signatures, Unlocking & OCR
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Safeguard confidential contracts or unlock files you own. Encrypt documents with 128-bit passwords, e-sign contracts directly with your mouse or stylus, and run OCR on scanned documents to make them searchable.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                  <Link to="/pdf-tools/$slug" params={{ slug: "protect-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🔒</span> Protect PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "unlock-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🔓</span> Unlock PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "sign-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>✍️</span> Sign PDF
                  </Link>
                  <Link to="/pdf-tools/$slug" params={{ slug: "ocr-pdf" }} className="font-semibold text-primary hover:underline flex items-center gap-1.5">
                    <span>🔍</span> OCR PDF
                  </Link>
                </div>
              </div>

              {/* Privacy & Enterprise Compliance */}
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 sm:p-8 space-y-3">
                <h3 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
                  <ShieldCheck className="size-6 text-emerald-600 dark:text-emerald-400" />
                  HIPAA, GDPR & Legal-Grade Document Confidentiality
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Traditional PDF websites require you to upload confidential tax forms, medical records, bank statements, or legal contracts to remote cloud servers. Picamini.online’s <strong>PDF Toolkit Pro</strong> works fundamentally differently: every page calculation, text extraction, page merge, and password encryption happens <strong>100% inside your local device's memory</strong> using HTML5 Canvas and WASM. Your confidential files never touch the internet.
                </p>
              </div>

              {/* Comparative Analysis Table */}
              <div className="space-y-4">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Comparison: Picamini.online PDF Toolkit Pro vs Traditional Cloud Tools
                </h3>
                <div className="overflow-x-auto rounded-2xl border border-border">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-secondary text-foreground font-bold">
                      <tr>
                        <th className="p-3.5 border-b">Feature</th>
                        <th className="p-3.5 border-b text-primary">Picamini.online PDF Toolkit Pro</th>
                        <th className="p-3.5 border-b">Traditional Cloud Web Tools</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border text-muted-foreground">
                      <tr>
                        <td className="p-3.5 font-semibold text-foreground">File Privacy</td>
                        <td className="p-3.5 font-bold text-emerald-600 dark:text-emerald-400">100% Local (No Upload)</td>
                        <td className="p-3.5">Uploaded to External Cloud</td>
                      </tr>
                      <tr>
                        <td className="p-3.5 font-semibold text-foreground">Processing Speed</td>
                        <td className="p-3.5 font-bold text-primary">Instant (Zero Upload Lag)</td>
                        <td className="p-3.5">Slow Upload & Download Delay</td>
                      </tr>
                      <tr>
                        <td className="p-3.5 font-semibold text-foreground">File Size Limits</td>
                        <td className="p-3.5 font-bold text-primary">Unlimited (Uses RAM)</td>
                        <td className="p-3.5">Restricted File Limits</td>
                      </tr>
                      <tr>
                        <td className="p-3.5 font-semibold text-foreground">Account Required</td>
                        <td className="p-3.5 font-bold text-primary">No Signup Needed</td>
                        <td className="p-3.5">Often Demands Account / Sub</td>
                      </tr>
                      <tr>
                        <td className="p-3.5 font-semibold text-foreground">Watermarks Added</td>
                        <td className="p-3.5 font-bold text-emerald-600 dark:text-emerald-400">Zero Watermarks</td>
                        <td className="p-3.5">Stamps Watermarks on Free Tier</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Professional Industry Use-Cases */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 space-y-6">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Industry Use-Cases for Private PDF Processing
                </h3>
                <div className="grid gap-6 sm:grid-cols-2 text-sm text-muted-foreground">
                  <div className="space-y-2">
                    <h4 className="font-bold text-foreground text-base">⚖️ Legal & Law Firms</h4>
                    <p className="leading-relaxed">
                      Attorneys and paralegals must maintain strict client confidentiality. Use <Link to="/pdf-tools/$slug" params={{ slug: "merge-pdf" }} className="text-primary underline font-semibold">Merge PDF</Link> to consolidate case exhibits, stamp page numbers with <Link to="/pdf-tools/$slug" params={{ slug: "page-numbers" }} className="text-primary underline font-semibold">Page Numbers</Link>, and e-sign filings using <Link to="/pdf-tools/$slug" params={{ slug: "sign-pdf" }} className="text-primary underline font-semibold">Sign PDF</Link> without exposing court documents to third-party servers.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-bold text-foreground text-base">🏥 Healthcare & Medical Records</h4>
                    <p className="leading-relaxed">
                      HIPAA compliance forbids uploading Patient Health Information (PHI). Healthcare administrators can redact sensitive lines in <Link to="/pdf-tools/$slug" params={{ slug: "edit-pdf" }} className="text-primary underline font-semibold">Edit PDF</Link>, split patient charts with <Link to="/pdf-tools/$slug" params={{ slug: "split-pdf" }} className="text-primary underline font-semibold">Split PDF</Link>, and encrypt records with <Link to="/pdf-tools/$slug" params={{ slug: "protect-pdf" }} className="text-primary underline font-semibold">Protect PDF</Link> 100% locally.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-bold text-foreground text-base">📊 Finance, Accounting & Tax Professionals</h4>
                    <p className="leading-relaxed">
                      Extract tax return tables into spreadsheets using <Link to="/pdf-tools/$slug" params={{ slug: "pdf-to-excel" }} className="text-primary underline font-semibold">PDF to Excel</Link>, shrink audit files with <Link to="/pdf-tools/$slug" params={{ slug: "compress-pdf" }} className="text-primary underline font-semibold">Compress PDF</Link>, and convert financial statements using <Link to="/pdf-tools/$slug" params={{ slug: "excel-to-pdf" }} className="text-primary underline font-semibold">Excel to PDF</Link>.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-bold text-foreground text-base">🎓 Education, Students & Researchers</h4>
                    <p className="leading-relaxed">
                      Combine research papers into thesis drafts, convert lecture slides with <Link to="/pdf-tools/$slug" params={{ slug: "ppt-to-pdf" }} className="text-primary underline font-semibold">PowerPoint to PDF</Link>, and make scanned book chapters searchable using <Link to="/pdf-tools/$slug" params={{ slug: "ocr-pdf" }} className="text-primary underline font-semibold">OCR PDF</Link>.
                    </p>
                  </div>
                </div>
              </div>

              {/* Technical Architecture Section */}
              <div className="space-y-4">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  The Technology Behind Browser-Based PDF Manipulation
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Traditional web PDF services rely on backend servers running Linux binaries like Ghostscript or Poppler. When millions of users upload files simultaneously, server queues choke and privacy risks skyrocket. Picamini.online’s <strong>PDF Toolkit Pro</strong> leverages modern WebAssembly (WASM) and JavaScript standards (PDF.js by Mozilla and PDF-Lib by Hopding) to perform rendering, byte manipulation, stream extraction, and cryptography directly in V8/Javascript engines.
                </p>
                <div className="grid gap-4 sm:grid-cols-3 text-sm">
                  <div className="rounded-xl border border-border p-4 bg-card">
                    <h4 className="font-bold text-foreground text-sm">🚀 Zero Latency</h4>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Eliminates network upload and download bottlenecks. Heavy 100MB PDF files process at gigabit local memory speeds.
                    </p>
                  </div>
                  <div className="rounded-xl border border-border p-4 bg-card">
                    <h4 className="font-bold text-foreground text-sm">🔌 Works Offline</h4>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Once loaded in your tab, tools like <Link to="/pdf-tools/$slug" params={{ slug: "edit-pdf" }} className="text-primary underline">Edit PDF</Link> and <Link to="/pdf-tools/$slug" params={{ slug: "merge-pdf" }} className="text-primary underline">Merge PDF</Link> continue functioning even without internet access.
                    </p>
                  </div>
                  <div className="rounded-xl border border-border p-4 bg-card">
                    <h4 className="font-bold text-foreground text-sm">🔐 True Zero Trust Security</h4>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      No cloud storage, no database logs, no file auto-deletion timers — because your bytes never leave your workstation.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step-by-Step PDF Workflows Guide */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 space-y-4">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Step-by-Step Guides for Common PDF Workflows
                </h3>
                
                <div className="space-y-4 text-sm text-muted-foreground">
                  <div>
                    <h4 className="font-bold text-foreground text-base">📌 How to Combine Multiple PDFs into One Document</h4>
                    <ol className="mt-1 list-decimal list-inside space-y-1">
                      <li>Open the <Link to="/pdf-tools/$slug" params={{ slug: "merge-pdf" }} className="text-primary underline font-semibold">Merge PDF</Link> tool.</li>
                      <li>Drag and drop your PDF files into the upload box or click to browse.</li>
                      <li>Drag thumbnails to arrange files in your desired page order.</li>
                      <li>Click "Merge PDF" to generate and download your combined file instantly.</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-bold text-foreground text-base">📌 How to Compress a Large PDF for Email Attachments</h4>
                    <ol className="mt-1 list-decimal list-inside space-y-1">
                      <li>Navigate to the <Link to="/pdf-tools/$slug" params={{ slug: "compress-pdf" }} className="text-primary underline font-semibold">Compress PDF</Link> tool.</li>
                      <li>Upload your high-resolution or multi-page PDF document.</li>
                      <li>Select your target compression level (Low, Recommended, or High Compression).</li>
                      <li>Click "Compress PDF" to reduce file size up to 80% with zero quality loss on text.</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-bold text-foreground text-base">📌 How to Password Protect & Encrypt a PDF</h4>
                    <ol className="mt-1 list-decimal list-inside space-y-1">
                      <li>Open the <Link to="/pdf-tools/$slug" params={{ slug: "protect-pdf" }} className="text-primary underline font-semibold">Protect PDF</Link> tool.</li>
                      <li>Drop your confidential contract, invoice, or personal document into the editor.</li>
                      <li>Enter a secure password to restrict unauthorized opening or editing.</li>
                      <li>Download your 128-bit AES encrypted PDF document.</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Extended FAQ Section */}
              <div className="space-y-4 pt-4">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Frequently Asked Questions (FAQ)
                </h3>
                <div className="space-y-3 text-sm">
                  <details className="group rounded-xl border border-border bg-card p-4 transition-all">
                    <summary className="cursor-pointer font-bold text-foreground list-none flex items-center justify-between">
                      Are my uploaded PDF files safe on Picamini.online?
                      <span className="text-primary transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      You never upload anything! All PDF processing (merging, splitting, editing, compressing, converting) happens client-side inside your own web browser. Your files stay on your machine at all times.
                    </p>
                  </details>

                  <details className="group rounded-xl border border-border bg-card p-4 transition-all">
                    <summary className="cursor-pointer font-bold text-foreground list-none flex items-center justify-between">
                      Is there any file size limit for merging or editing PDFs?
                      <span className="text-primary transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      No artificial file size limits! Since processing uses your device’s local memory (RAM), you can merge, split, or compress large PDF files that would fail on server-based converters.
                    </p>
                  </details>

                  <details className="group rounded-xl border border-border bg-card p-4 transition-all">
                    <summary className="cursor-pointer font-bold text-foreground list-none flex items-center justify-between">
                      Can I edit existing text in a PDF document?
                      <span className="text-primary transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      Yes! Our <Link to="/pdf-tools/$slug" params={{ slug: "edit-pdf" }} className="text-primary underline font-semibold">Edit PDF</Link> tool features a live text engine that allows you to click into existing text lines and retype them, as well as add new text boxes, shapes, watermarks, and freehand drawings.
                    </p>
                  </details>

                  <details className="group rounded-xl border border-border bg-card p-4 transition-all">
                    <summary className="cursor-pointer font-bold text-foreground list-none flex items-center justify-between">
                      How does the PDF Compressor work?
                      <span className="text-primary transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      Our <Link to="/pdf-tools/$slug" params={{ slug: "compress-pdf" }} className="text-primary underline font-semibold">Compress PDF</Link> tool re-encodes embedded image streams, strips unnecessary metadata, and optimizes font structures locally to reduce file sizes by up to 80% while retaining text sharpness.
                    </p>
                  </details>

                  <details className="group rounded-xl border border-border bg-card p-4 transition-all">
                    <summary className="cursor-pointer font-bold text-foreground list-none flex items-center justify-between">
                      Can I convert scanned PDFs into searchable text?
                      <span className="text-primary transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      Yes! Using our <Link to="/pdf-tools/$slug" params={{ slug: "ocr-pdf" }} className="text-primary underline font-semibold">OCR PDF</Link> tool, Optical Character Recognition runs inside your browser to extract searchable, copyable text from scanned images and unsearchable PDF pages.
                    </p>
                  </details>

                  <details className="group rounded-xl border border-border bg-card p-4 transition-all">
                    <summary className="cursor-pointer font-bold text-foreground list-none flex items-center justify-between">
                      Do these tools work on iPhones, iPads, and Android devices?
                      <span className="text-primary transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      Yes! All tools are fully mobile-responsive and work smoothly in Safari, Chrome, Firefox, and Edge browsers on iOS, Android, macOS, Windows, and Linux.
                    </p>
                  </details>
                </div>
              </div>
            </article>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
